import React from 'react';
import { Lock } from 'lucide-react';
import { BSI_SERVICE_TILES, type BsiTabKey } from './helpers';
import BsiPendingSignBanner, { usePendingSignSummary } from './BsiPendingSignBanner';

type Props = {
  sessionValid: boolean;
  onOpen: (key: BsiTabKey) => void;
};

const groupTitle: Record<string, string> = {
  auth: 'ورود و اتصال',
  accounts: 'حساب و گردش',
  transfer: 'انتقال وجه',
  tools: 'استعلام و ابزار',
};

const BsiMetroHome: React.FC<Props> = ({ sessionValid, onOpen }) => {
  const groups = ['auth', 'accounts', 'transfer', 'tools'] as const;
  const { signableCount } = usePendingSignSummary(sessionValid);

  return (
    <div className="space-y-8 lg:col-span-3">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-bl from-slate-50 via-white to-amber-50/40 px-5 py-4">
        <h2 className="text-lg font-semibold text-slate-900">خدمات اینترنت‌بانک صادرات</h2>
        <p className="mt-1 text-sm text-slate-600">
          مثل پنل بانک، روی هر باکس بزنید تا وارد همان خدمت شوید.
          {!sessionValid && ' برای بیشتر خدمات ابتدا از باکس ورود متصل شوید.'}
        </p>
      </div>

      {sessionValid && signableCount > 0 ? (
        <BsiPendingSignBanner signableCount={signableCount} onNavigatePending={() => onOpen('pending')} />
      ) : null}

      {groups.map((group) => {
        const tiles = BSI_SERVICE_TILES.filter((t) => t.group === group);
        if (!tiles.length) return null;
        return (
          <section key={group} className="space-y-3">
            <h3 className="text-sm font-medium text-slate-500">{groupTitle[group]}</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {tiles.map((tile) => {
                const Icon = tile.icon;
                const locked = Boolean(tile.requiresSession && !sessionValid);
                const pendingBadge = tile.key === 'pending' && signableCount > 0 ? signableCount : 0;
                return (
                  <button
                    key={tile.key}
                    type="button"
                    disabled={locked && tile.key !== 'login'}
                    onClick={() => onOpen(tile.key)}
                    className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${tile.tone} p-4 text-start text-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-55`}
                  >
                    <div className="pointer-events-none absolute -left-6 -top-6 h-24 w-24 rounded-full bg-white/10 transition group-hover:scale-110" />
                    <div className="pointer-events-none absolute -bottom-8 -right-4 h-28 w-28 rounded-full bg-black/10" />
                    {pendingBadge > 0 ? (
                      <span className="absolute left-3 top-3 z-10 rounded-full bg-white px-2 py-0.5 text-xs font-bold text-amber-700 shadow">
                        {pendingBadge}
                      </span>
                    ) : null}
                    <div className="relative flex items-start justify-between gap-3">
                      <div>
                        <div className="text-base font-semibold">{tile.title}</div>
                        <p className="mt-1 text-xs leading-5 text-white/85">
                          {tile.key === 'pending' && pendingBadge > 0
                            ? `${pendingBadge} مورد در انتظار امضا`
                            : tile.description}
                        </p>
                      </div>
                      <div className="rounded-xl bg-white/15 p-2 backdrop-blur-sm">
                        {locked && tile.key !== 'login' ? <Lock className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default BsiMetroHome;
